<script lang="ts">
    import { Component, Prop, Vue } from "vue-facing-decorator";
    import StepperComponent from "@/components/atoms/stepper/Stepper.vue";
    import { CurrencyHandler } from "webshop-modules";
    import { TicketType } from "webshop-api";
    import { useBestSeatStore } from "@/domain/ticket-selection/application/BestSeat.store";
    import {
        PriceLevelState,
        PromotionHoldState,
        TicketTypeState
    } from "@/domain/ticket-selection/entities/ticket-selection";
    import { StepperState } from "@/components/atoms/stepper/stepper-state.interface";

    @Component({
        name: 'TicketTypeComponent',
        components: { StepperComponent }
    })
    export default class TicketTypeComponent extends Vue {

        private readonly bestSeatStore = useBestSeatStore();

        public readonly CurrencyHandler = new CurrencyHandler(
            this.bestSeatStore.currency ?? '',
            this.bestSeatStore.currencyOptions?.currency ?? '',
            this.bestSeatStore.currencyOptions?.conversionRate ?? '1',
            this.bestSeatStore.currencyOptions?.showDoubleCurrency ?? false
        );

        @Prop()
        public data!: TicketType;

        @Prop()
        public connectedPriceLevel!: number;


        public get minTicketAmount(): number {
            return this.data.minTicketsPerOrder ? this.data.minTicketsPerOrder : 0;
        }

        public get priceLevelState(): PriceLevelState | undefined {
            return this.bestSeatStore.priceLevels.find(
                (priceLevel: PriceLevelState) => priceLevel.priceLevelId === this.connectedPriceLevel
            );
        }

        public get stepperState(): StepperState {
            return {
                step: this.ticketTypeState.step,
                amount: this.ticketTypeState.amount,
                canDecrease: this.ticketTypeState.canDecrease,
                canIncrease: this.ticketTypeState.canIncrease,
            };
        }

        public get ticketTypeState(): TicketTypeState {
            const priceLevel = this.bestSeatStore.priceLevels
                .find((priceLevel: PriceLevelState) => priceLevel.priceLevelId === this.connectedPriceLevel);
            const promotion = priceLevel!.promotionHolds
                .find((promotion: PromotionHoldState) => promotion.active);

            return promotion!.holds[0].ticketTypes.find(
                (ticketType: TicketTypeState) => ticketType.priceId === this.data.priceId
            )!;
        }

        public handleStepperStateChange(state: StepperState): void {
            const { activatePriceLevel, handleTicketTypeChange } = this.bestSeatStore;
            activatePriceLevel(this.priceLevelState);
            handleTicketTypeChange({ state: this.ticketTypeState, stepper: state });
        }
    }
</script>

<template>
    <div class="m-ticketType">
        <div class="m-ticketType__data">
            <h5 class="m-ticketType__heading">{{ data.text }}</h5>
            <div class="m-ticketType__price">
                <p :data-qa="'ticket-type-price'"
                    class="m-ticketType__pricePrimary">
                    {{
                        CurrencyHandler.displayPrimaryCurrency(data.price ? data.price : 0)
                    }} <br>
                </p>
                <p v-if="CurrencyHandler.isSecondaryCurrencyDisplayed" class="m-ticketType__pricePrimary">
                    {{
                        CurrencyHandler.displaySecondaryCurrency(data.price ? data.price : 0)
                    }}
                </p>
            </div>
        </div>

        <stepper-component :state="stepperState"
                           v-on:state-change="handleStepperStateChange">
        </stepper-component>

        <p class="m-ticketType__additional" v-if="data.description">* {{ data.description }}</p>
    </div>
</template>
